import { Component, OnInit, inject } from '@angular/core';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {first, map, mergeMap, tap} from 'rxjs/operators';
import {ClientService} from '../../../client/client.service';
import {Client} from '../../../client/client';
import {Location} from '@angular/common';
import {ClientMelding} from '../../../client-melding/client-melding';
import {ClientMeldingService} from '../../../client-melding/client-melding.service';
import {AuthService} from '../../../core/auth/auth.service';
import User from '../../../core/auth/user';
import {AlgemeneMelding} from '../../../algemene-melding/algemene-melding';
import {AlgemeneMeldingService} from '../../../algemene-melding/algemene-melding.service';

@Component({
    templateUrl: './meldingen.page.html',
    styleUrls: ['./meldingen.page.scss'],
    standalone: false
})
export class MeldingenPage implements OnInit {
  private authService = inject(AuthService);
  private clientService = inject(ClientService);
  private algemeneMeldingService = inject(AlgemeneMeldingService);
  private clientMeldingService = inject(ClientMeldingService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);

  user$: Observable<User>;
  client$: Observable<Client>;
  algemeneMeldingen$: Subject<Array<AlgemeneMelding>> = new ReplaySubject<Array<AlgemeneMelding>>(1);
  clientMeldingen$: Subject<Array<ClientMelding>> = new ReplaySubject<Array<ClientMelding>>(1);

  ngOnInit(): void {
    this.user$ = this.authService.getUser$().pipe(first());
    this.client$ = this.route.paramMap
      .pipe(
        map(paramMap => paramMap.get('clientId')),
        mergeMap(id => this.clientService.getClientById$(id)),
      );
    this.client$
      .pipe(
        first(),
        mergeMap(client => this.getClientMeldingen$(client)),
      ).subscribe();
    this.getAlgemeneMeldingen$().subscribe();
  }

  canManageAlgemeneMeldingen(user: User): boolean {
    return user.isRegioverantwoordelijke();
  }

  onAlgemeneMeldingEdited([melding, content, actiefVanDag, actiefTotDag]: [AlgemeneMelding, string, string, string]): void {
    this.algemeneMeldingService
      .putAlgemeneMelding$(melding.getId(), content, actiefVanDag, actiefTotDag)
      .pipe(mergeMap(() => this.getAlgemeneMeldingen$()))
      .subscribe();
  }

  onAlgemeneMeldingDeleted(melding: AlgemeneMelding): void {
    this.algemeneMeldingService
      .deleteAlgemeneMelding$(melding.getId())
      .pipe(mergeMap(() => this.getAlgemeneMeldingen$()))
      .subscribe();
  }

  onAlgemeneMeldingAdded([content, actiefVanDag, actiefTotDag]: [string, string, string]): void {
    this.algemeneMeldingService
      .postAlgemeneMelding$(content, actiefVanDag, actiefTotDag)
      .pipe(mergeMap(() => this.getAlgemeneMeldingen$()))
      .subscribe();
  }

  onClientMeldingEdited(client: Client, [melding, content]: [ClientMelding, string]): void {
    const dienstId = this.getDienstId();
    this.clientMeldingService
      .putClientMelding$(melding.getId(), content, dienstId)
      .pipe(mergeMap(() => this.getClientMeldingen$(client)))
      .subscribe();
  }

  onClientMeldingDeleted(client: Client, melding: ClientMelding): void {
    this.clientMeldingService
      .deleteClientMelding$(melding.getId())
      .pipe(mergeMap(() => this.getClientMeldingen$(client)))
      .subscribe();
  }

  onClientMeldingAdded(client: Client, content: string): void {
    const dienstId = this.getDienstId();
    this.clientMeldingService
      .postClientMelding$(client.getId(), content, dienstId)
      .pipe(mergeMap(() => this.getClientMeldingen$(client)))
      .subscribe();
  }

  navigateBack(): void {
    this.location.back();
  }

  private getAlgemeneMeldingen$(): Observable<Array<AlgemeneMelding>> {
    return this.algemeneMeldingService
      .getAlgemeneMeldingen$()
      .pipe(tap(sortedAlgemeneMeldingen => this.algemeneMeldingen$.next(sortedAlgemeneMeldingen)));
  }

  private getClientMeldingen$(client: Client): Observable<Array<ClientMelding>> {
    return this.clientMeldingService
      .getClientMeldingen$(client.getId())
      .pipe(tap(clientMeldingen => this.clientMeldingen$.next(clientMeldingen)));
  }

  private getDienstId(): string | null {
    return this.route.snapshot.queryParamMap.get('dienstId');
  }
}
